import type { FieldDef } from './types'

type Values = Record<string, unknown>
type L = { en: string; ar: string | null }

/**
 * API item (or nothing, for a new item) → form values. Every input gets a
 * defined value: nulls become '' and absent translations { en: '', ar: '' }.
 * The shared zod schema turns blanks back into nulls on submit, so the form
 * never has to know which fields are optional.
 */
export function toFormValues(fields: FieldDef[], src: Values = {}): Values {
  const out: Values = {}
  for (const f of fields) {
    switch (f.kind) {
      case 'section':
        Object.assign(out, toFormValues(f.fields, src))
        break
      case 'roadmapBody':
        out.body = src.body ?? ''
        out.phaseTitlesAr = src.phaseTitlesAr ?? []
        break
      case 'boolean':
        out[f.name] = Boolean(src[f.name])
        break
      case 'select':
        out[f.name] = src[f.name] ?? f.options[0]
        break
      case 'l10n': {
        const v = src[f.name] as L | null | undefined
        out[f.name] = { en: v?.en ?? '', ar: v?.ar ?? '' }
        break
      }
      case 'l10nList':
        out[f.name] = ((src[f.name] as L[] | undefined) ?? []).map((v) => ({ en: v.en, ar: v.ar ?? '' }))
        break
      case 'stringList':
        out[f.name] = (src[f.name] as string[] | undefined) ?? []
        break
      case 'objects':
        out[f.name] = ((src[f.name] as Values[] | undefined) ?? []).map((v) => toFormValues(f.fields, v))
        break
      default:
        out[f.name] = src[f.name] ?? ''
    }
  }
  return out
}

const isL = (v: unknown): v is L =>
  typeof v === 'object' && v !== null && 'en' in v && 'ar' in v && Object.keys(v).length === 2

/** Paths of translatable fields with English but no Arabic, skipping English-by-design ones. */
export function missingArabic(value: unknown, skip: string[] = [], path: string[] = []): string[] {
  const here = path.join('.')
  if (skip.includes(here)) return []
  if (isL(value)) return value.en && !value.ar ? [here] : []
  if (Array.isArray(value)) return [...new Set(value.flatMap((v) => missingArabic(v, skip, path)))]
  if (value && typeof value === 'object') {
    return [...new Set(Object.entries(value).flatMap(([k, v]) => missingArabic(v, skip, [...path, k])))]
  }
  return []
}

export function slugify(s: string): string {
  return s.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80)
}

export function getPath(obj: Values, path: string): unknown {
  return path.split('.').reduce<unknown>((o, k) => (o as Values | undefined)?.[k], obj)
}
